import React, { ComponentProps, forwardRef, useEffect } from 'react'
import { useDebounce } from 'react-use'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase'
import {
  TInputChangeHandler,
  useAutocompleteInputState,
  useAutocompleteLazyQuery,
} from 'transfix-ui/components/AutocompleteBase/hooks'
import {
  IAutocompleteBaseOption,
  TAutocompleteBaseChangeHandler,
} from 'transfix-ui/components/AutocompleteBase/types'
import { isAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/utils/typeGuards'
import { IShipperFacilityResultFragment } from './graphql/ShipperFacilityResultFragment'
import { useStopFacilitiesLazyQuery, IStopFacilitiesQuery } from './graphql/StopFacilities'
import { getOptionLabel } from './utils'

const getComputedValue = (
  options: IAutocompleteBaseOption[],
  value: Pick<IAutocompleteBaseOption, 'id'> | null
): IAutocompleteBaseOption | null => {
  const { id: idProp } = value || {}
  const option = options.find(({ id }) => id === (idProp || value))
  return option || null
}

export interface IAutocompleteFacilityProps<
  V extends Pick<IAutocompleteBaseOption, 'id' | 'text'> = Pick<
    IAutocompleteBaseOption,
    'id' | 'text'
  >
>
  extends Omit<
    ComponentProps<typeof AutocompleteBase>,
    'value' | 'onChange' | 'options' | 'loading'
  > {
  value: V | null
  shipperApplicationId: string
  shipperFacility?: IShipperFacilityResultFragment | null
  required?: boolean
  onChange: (newVal: V | null) => void
  inputError?: boolean
  inputValidationMessage?: string
  dataTest?: string
  showAddressDetail?: boolean
}

export const AutocompleteFacility = forwardRef<HTMLInputElement, IAutocompleteFacilityProps>(
  (
    {
      clearOnBlur = false,
      dataTest = 'AutocompleteFacility',
      inputError,
      inputValidationMessage,
      label = 'Facility',
      onChange,
      placeholder = 'Search for a facility',
      required,
      shipperApplicationId,
      shipperFacility,
      showAddressDetail,
      value: valueProp,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useAutocompleteInputState('')
    const shipperFacilityObject = shipperFacility?.uuid
      ? {
          id: shipperFacility.uuid,
          text: getOptionLabel(
            shipperFacility.facility?.name,
            shipperFacility.facility?.location.city,
            shipperFacility.facility?.location.stateCode
          ),
        }
      : null
    const [refetch, { options = [], loading }] = useAutocompleteLazyQuery(
      useStopFacilitiesLazyQuery,
      {
        variables: {
          shipperApplicationId,
          searchTerm: inputValue,
          ...(showAddressDetail ? { searchAddress: true } : {}),
        },
      },
      {
        value: shipperFacilityObject || valueProp || null,
        getOptionsConfig: data => {
          const { facilities = [] } = data || {}
          return {
            groupIndex: 1,
            options: facilities,
            mapOption: ({
              name = '',
              pickupAppointmentType,
              deliveryAppointmentType,
              shipperFacilities = [],
            }: IStopFacilitiesQuery['facilities'][number]) => {
              const [firstFacility] = shipperFacilities || []
              const { facility, uuid: id } = firstFacility || {}
              const { location } = facility || {}
              const { address, addressLine2, city, stateCode } = location || {}
              const addressDetail =
                address && addressLine2 ? address.concat(', ', addressLine2) : address
              const text = showAddressDetail
                ? getOptionLabel(name, city, stateCode, addressDetail)
                : getOptionLabel(name, city, stateCode)
              return {
                id,
                pickupAppointmentType,
                deliveryAppointmentType,
                text,
                city,
                stateCode,
                address,
              }
            },
          }
        },
      }
    )

    /**
     * Refetch when this component mounts, and only once
     */
    useEffect(() => {
      if (shipperApplicationId) {
        refetch({ variables: { shipperApplicationId, searchTerm: '' } })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipperApplicationId])

    /**
     * Refetch when the input value changes
     */
    useDebounce(
      () => {
        if (inputValue && shipperApplicationId) {
          refetch({ variables: { shipperApplicationId, searchTerm: inputValue } })
        }
      },
      250,
      [inputValue]
    )

    const changeHandler: TAutocompleteBaseChangeHandler = (_, newVal) => {
      if (isAutocompleteBaseOption(newVal)) {
        onChange(newVal)
      } else {
        onChange(null)
      }
    }

    const inputChangeHandler: TInputChangeHandler = (e, newVal) => {
      /**
       * MUI triggers an onChange event with `null` value when
       * trying to press delete/backspace when there's a value
       *
       * Because this is an invalid event, we ignore it
       */
      if (e) {
        setInputValue(e, newVal)
      }
    }

    const value = getComputedValue(options, valueProp)

    /**
     * Set input value by default
     */
    useEffect(() => {
      if (!inputValue && shipperApplicationId) {
        refetch()
        setInputValue({} as any, value?.text || '')
      }
    }, [inputValue, setInputValue, value?.text, refetch])

    return (
      <AutocompleteBase
        {...props}
        label={label}
        data-test={dataTest}
        ref={ref}
        value={value}
        onChange={changeHandler}
        inputValue={inputValue}
        onInputChange={inputChangeHandler}
        options={options}
        loading={loading}
        placeholder={placeholder}
        loadingText='Loading...'
        selectOnFocus
        autoSelect
        clearOnBlur={clearOnBlur}
        required={required}
        inputError={inputError}
        inputValidationMessage={inputValidationMessage}
      />
    )
  }
)

export default AutocompleteFacility
